import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DocumentUpload } from '@/components/DocumentUpload';
import { FileText, Upload, X, StickyNote } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Document {
  filename: string;
  content: string;
  type: string;
}

interface Step2DocumentsAndNotesProps {
  additionalDocuments: Document[];
  revisionNotes: string;
  onDocumentsChange: (documents: Document[]) => void;
  onNotesChange: (notes: string) => void;
}

export const Step2DocumentsAndNotes: React.FC<Step2DocumentsAndNotesProps> = ({
  additionalDocuments,
  revisionNotes,
  onDocumentsChange,
  onNotesChange
}) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedChanges, setParsedChanges] = useState<any[]>([]);
  const [showChangesSummary, setShowChangesSummary] = useState(false);

  const handleDocumentUpload = async (content: string, filename?: string, fileUrl?: string) => {
    setIsProcessing(true);
    
    const newDocument: Document = {
      filename: filename || 'uploaded-document',
      content,
      type: filename?.split('.').pop()?.toLowerCase() || 'unknown'
    };
    
    // Parse document markup if it's a Word document or contains markup
    if (filename?.endsWith('.docx') || content.includes('[Comment:') || content.includes('{~') || content.includes('{+')) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await supabase.functions.invoke('parse-document-markup', {
          body: {
            document_content: content,
            document_type: filename?.endsWith('.docx') ? 'docx' : 'txt',
            filename: filename || 'uploaded-document'
          },
          headers: {
            Authorization: `Bearer ${session?.access_token}`
          }
        });

        if (response.data?.success) {
          const markup = response.data.markup;
          const changes = [
            ...markup.tracked_changes.map((change: any) => ({
              type: 'tracked_change',
              ...change,
              source: filename
            })),
            ...markup.comments.map((comment: any) => ({
              type: 'comment',
              ...comment,
              source: filename
            })),
            ...markup.suggestions.map((suggestion: any) => ({
              type: 'suggestion',
              ...suggestion,
              source: filename
            }))
          ];
          
          setParsedChanges(prev => [...prev, ...changes]);
          
          if (changes.length > 0) {
            toast({
              title: "Document Markup Detected",
              description: `Found ${changes.length} tracked changes, comments, and suggestions in ${filename}`,
            });
            setShowChangesSummary(true);
          }
        }
      } catch (error) {
        console.error('Error parsing document markup:', error);
      }
    }
    
    onDocumentsChange([...additionalDocuments, newDocument]);
    setIsProcessing(false);
  };

  const removeDocument = (index: number) => {
    const updatedDocuments = additionalDocuments.filter((_, i) => i !== index);
    onDocumentsChange(updatedDocuments);
  };

  const getFileTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'doc':
      case 'docx':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'txt':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Additional Documents Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Additional Documents
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Upload reference materials, job descriptions, or other documents to help guide the revision process.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentUpload
            onUploadComplete={handleDocumentUpload}
            acceptedTypes={[".pdf",".doc",".docx",".txt"]}
            title="Upload Reference Documents"
            description="PDF, Word, or text files with tracked changes and comments (max 10MB each)"
          />

          {isProcessing && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="text-sm text-blue-700">Processing document markup...</span>
            </div>
          )}

          {/* Changes Summary */}
          {showChangesSummary && parsedChanges.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Detected Changes Summary
                <Badge variant="outline">{parsedChanges.length} items</Badge>
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {parsedChanges.map((change, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Badge variant="secondary" className="mt-0.5 text-xs">
                      {change.type.replace('_', ' ')}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {change.type === 'comment' ? change.text : 
                         change.type === 'suggestion' ? `${change.original_text} → ${change.suggested_text}` :
                         change.original_text}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {change.location} • {change.source}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> These changes will be automatically processed when you proceed to the next step. 
                  The AI will create specific revision tasks based on all detected markup and your written instructions.
                </p>
              </div>
            </div>
          )}

          {additionalDocuments.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Uploaded Documents</h4>
              <div className="space-y-2">
                {additionalDocuments.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.content.length} characters
                        </p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getFileTypeColor(doc.type)}`}
                      >
                        {doc.type.toUpperCase()}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDocument(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revision Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Revision Instructions
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Provide detailed instructions for how you want the resume to be revised. Be specific about changes, additions, or improvements.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Example: Update the Skills section to include Python and React. Quantify achievements in the Experience section with specific metrics. Rewrite the Professional Summary to focus on data analysis rather than general management..."
            value={revisionNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            className="min-h-32 resize-none"
          />
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Pro Tips for Better Results</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Be specific about which sections to modify</li>
              <li>• Include target keywords for ATS optimization</li>
              <li>• Mention specific metrics or achievements to add</li>
              <li>• Specify the tone and style preferences</li>
              <li>• Reference uploaded documents when relevant</li>
            </ul>
          </div>

          {revisionNotes && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <div className="h-4 w-4 bg-green-500 rounded-full mt-0.5 flex-shrink-0"></div>
                <div className="text-sm">
                  <p className="font-medium text-green-900">Instructions Ready</p>
                  <p className="text-green-700">
                    {revisionNotes.length} characters of revision instructions provided.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {(additionalDocuments.length > 0 || revisionNotes) && (
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="text-lg">Session Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Additional Documents:</span>
              <Badge variant="outline">{additionalDocuments.length} files</Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span>Revision Instructions:</span>
              <Badge variant="outline">
                {revisionNotes ? `${revisionNotes.length} characters` : 'None provided'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};